import { MapSkeleton } from '@/components/Skeletons/MapSkeleton';
import { queries } from '@/utils/queryOptions';
import { createFileRoute } from '@tanstack/react-router';
import { ErrorPage } from '@/components/ErrorPage';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import z from 'zod';
import { add, format } from 'date-fns';
import {
	mapRoomIdTransCounts,
	premiseToFloorNodes,
} from '@/utils/floorMapping';
import { PremiseMap } from '@/components/PremiseMap';
import { DateRangePicker } from '@frontend/ui/components/date-picker-range';
import { useMemo } from 'react';
import { ButtonGroup } from '@frontend/ui/components/button-group';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@frontend/ui/components/dropdown-menu';
import { Button } from '@frontend/ui/components/button';
import { roomGroupUsageFN } from '@/server/get';
import { getThresholdKeyFromUsage } from '@/utils/thresholds';
import { Threshold } from '@frontend/api-client-user/types';
import { MapNode } from '@frontend/map-engine-oop/types';

const searchSchema = z.object({
	start: z
		.string()
		.default(format(add(new Date(), { days: -30 }), 'yyyy-MM-dd')),
	end: z.string().default(format(new Date(), 'yyyy-MM-dd')),
	posX: z.number().optional(),
	posY: z.number().optional(),
	scale: z.number().optional(),
	showNames: z.boolean().default(false),
	showCounts: z.boolean().default(true),
	showIcons: z.boolean().default(true),
	roomGroupId: z.string().optional(),
});

export const Route = createFileRoute('/_protected/map')({
	validateSearch: searchSchema,
	component: RouteComponent,
	beforeLoad: () => ({ crumb: 'Map' }),
	ssr: false,
	loaderDeps: ({ search }) => ({
		sectionId: search.sectionId,
		start: search.start,
		end: search.end,
	}),
	loader: async ({ context, deps }) => {
		await context.queryClient.ensureQueryData(
			queries.sections.map(deps.sectionId),
		);
		await context.queryClient.ensureQueryData(
			queries.usage.list(deps.sectionId, deps.start, deps.end),
		);
		await context.queryClient.ensureQueryData(queries['room-groups'].list);
		await context.queryClient.ensureQueryData(
			queries['room-groups'].thresholds,
		);
		await context.queryClient.ensureQueryData(
			queries['room-groups-usage'].list(deps.start, deps.end),
		);
	},
	errorComponent: ({ error }) => <ErrorPage message={error.message} />,
	pendingComponent: MapSkeleton,
});

function RouteComponent() {
	const {
		sectionId,
		start,
		end,
		showIcons,
		showCounts,
		showNames,
		posX,
		posY,
		scale,
		roomGroupId,
	} = Route.useSearch();
	const navigate = Route.useNavigate();

	const section = useSuspenseQuery(queries.sections.map(sectionId));
	const { data: thresholds } = useSuspenseQuery(
		queries['room-groups'].thresholds,
	);
	const { data: usage } = useSuspenseQuery(
		queries.usage.list(sectionId, start, end),
	);
	const { data: roomGroupsUsage } = useSuspenseQuery(
		queries['room-groups-usage'].list(start, end),
	);
	const { data: roomGroups } = useSuspenseQuery(queries['room-groups'].list);

	const premise = section.data.premises[0];
	const roomGroup = roomGroups.items.find((x) => x.id === roomGroupId);

	if (!premise) throw new Error('No Premise Found');

	const transMap = mapRoomIdTransCounts(usage.transactions);
	const floors = premiseToFloorNodes(premise.floors, transMap, roomGroup);

	const thresholdKeys = thresholds.items.reduce(
		(acc, t) => {
			acc[t.id] = t.usageThresholds;
			return acc;
		},
		{} as Record<string, Threshold[]>,
	);

	for (const u of roomGroupsUsage.items) {
		const tkey = thresholdKeys[u.id];
		const threshold = getThresholdKeyFromUsage(u.usage, tkey);

		const rooms =
			roomGroups.items
				.filter((x) => (roomGroup ? x.id === roomGroup.id : true))
				.find((x) => x.id === u.id)?.rooms || [];
		const roomIds = rooms.map((x) => x.id);

		for (const floor of floors) {
			const roomIndex = floor.nodes.reduce(
				(acc, n, i) => {
					if (!acc) acc = [];

					if (roomIds.includes(n.id)) {
						acc.push([i, n]);
					}

					return acc;
				},
				[] as [number, MapNode][],
			);

			for (const [index] of roomIndex) {
				if (
					floor.nodes[index] &&
					threshold !== 'Empty' &&
					threshold !== 'Unknown'
				) {
					floor.nodes[index].status = threshold;
					floor.nodes[index].roomGroupId = u.id;
				}
			}
		}
	}

	const nodeFloors = useMemo(
		() => floors.slice().sort((a, b) => b.number - a.number),
		[sectionId, start, end, roomGroup],
	);

	const setRoomGroup = (groupId: string | undefined) =>
		navigate({ search: (s) => ({ ...s, roomGroupId: groupId }) });

	return (
		<>
			<div className="absolute top-0 left-0 right-0 m-auto flex justify-center p-2 z-10">
				<ButtonGroup>
					<DateRangePicker
						className="h-auto bg-background dark:bg-background hover:bg-muted dark:hover:bg-muted"
						initialDateFrom={start}
						initialDateTo={end}
						onUpdate={({ range }) =>
							navigate({
								replace: true,
								search: (prev) => ({
									...prev,
									start: format(range.from, 'yyyy-MM-dd'),
									end: format(range.to ?? range.from, 'yyyy-MM-dd'),
								}),
							})
						}
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className="bg-background dark:bg-background hover:bg-muted dark:hover:bg-muted"
							>
								Group: {roomGroup?.name ?? 'None'}
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuCheckboxItem
								checked={!roomGroup}
								onCheckedChange={(checked) =>
									checked && setRoomGroup(undefined)
								}
							>
								None
							</DropdownMenuCheckboxItem>
							{roomGroupsUsage.items.map((item) => (
								<DropdownMenuCheckboxItem
									key={item.id}
									checked={roomGroup?.id === item.id}
									onCheckedChange={(checked) =>
										checked && setRoomGroup(item.id)
									}
								>
									{item.name}
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</ButtonGroup>
			</div>
			<PremiseMap
				roomGroups={roomGroups.items}
				usage={usage}
				thresholds={thresholdKeys}
				floorNodes={nodeFloors}
				initialState={{ showIcons, showCounts, showNames, posX, posY, scale }}
			/>
		</>
	);
}
